import mixpanel from 'mixpanel-browser';

/**
 * analytics.ts — M-74: Product analytics instrumentation via Mixpanel.
 *
 * All functions are no-ops when VITE_MIXPANEL_TOKEN is absent (e.g. local dev
 * without the env var set) and never throw — a tracking failure must never
 * disrupt the user experience.
 *
 * Event naming convention: Title Case noun phrase describing what happened.
 * Property keys: camelCase, no PII beyond uid (which Mixpanel treats as the
 * distinct_id and never appears in event payloads sent to the browser).
 */

let _initialised = false;

/** @internal — exported for tests only */
export function _isInitialised(): boolean {
  return _initialised;
}

/** @internal — exported for tests only; resets module state between test cases */
export function _resetForTests(): void {
  _initialised = false;
}

/**
 * Call once at app boot (before rendering) to initialise Mixpanel.
 * Reads VITE_MIXPANEL_TOKEN from the Vite env. No-ops gracefully when absent.
 */
export function initAnalytics(): void {
  const token = import.meta.env.VITE_MIXPANEL_TOKEN as string | undefined;
  if (!token) return;
  try {
    mixpanel.init(token, {
      // Don't send data until the user has accepted the cookie consent banner.
      // CookieConsent.tsx calls mixpanel.opt_in_tracking() after acceptance.
      opt_out_tracking_by_default: true,
      persistence: 'localStorage',
      // Scrub IP to reduce GDPR surface — geo is inferred server-side only.
      ip: false,
    });
    _initialised = true;
  } catch {
    // Non-fatal — analytics must never break the app.
  }
}

export function identifyUser(uid: string, props: { displayName?: string } = {}): void {
  if (!_initialised) return;
  try {
    mixpanel.identify(uid);
    if (props.displayName) {
      mixpanel.people.set({ $name: props.displayName });
    }
  } catch {
    // Non-fatal.
  }
}

/**
 * GRC-24: Call after the user accepts the cookie consent banner.
 * Mixpanel is initialised with opt_out_tracking_by_default: true, so no data
 * flows until this function is invoked.
 */
export function enableAnalytics(): void {
  if (!_initialised) return;
  try {
    mixpanel.opt_in_tracking();
  } catch {
    // Non-fatal.
  }
}

/**
 * Reset the Mixpanel identity — call on sign-out so the next session starts
 * as a fresh anonymous profile.
 */
export function resetAnalytics(): void {
  if (!_initialised) return;
  try {
    mixpanel.reset();
  } catch {
    // Non-fatal.
  }
}

export function trackLogin(method: string): void {
  _track('User Logged In', { method });
}

export function trackSignUp(method: string): void {
  _track('User Signed Up', { method });
}

export interface TrackEventCreatedProps {
  hasDate: boolean;
  hasLocation: boolean;
  participantCount: number;
  vibeCount: number;
}

export function trackEventCreated(props: TrackEventCreatedProps): void {
  _track('Event Created', props);
}

export function trackPlanShared(method: string): void {
  _track('Plan Shared', { method });
}

/**
 * Fired when a participant saves their venue picks during the voting phase.
 * Forms the middle step of the north-star funnel:
 *   event_created → Vote Submitted (≥3 distinct uids) → status_generated
 *
 * @param eventId - Firestore event document ID
 * @param uid     - Firebase uid of the voter
 */
export function trackVoteSubmitted(eventId: string, uid: string): void {
  _track('Vote Submitted', { eventId, uid });
}

export interface TrackDiscoveryItemClickedProps {
  itemId: string;
  itemName: string;
  category: string;
}

export function trackDiscoveryItemClicked(props: TrackDiscoveryItemClickedProps): void {
  _track('Discovery Item Clicked', props);
}

export interface TrackDiscoveryPlanHereProps {
  itemId: string;
  itemName: string;
  category: string;
  surface: string;
}

export function trackDiscoveryPlanHere(props: TrackDiscoveryPlanHereProps): void {
  _track('Discovery Plan Here', props);
}

/** M-451: Actions the host can take during the complementary stop review phase. */
export type ComplementaryHostAction = 'keep' | 'remove' | 'replace' | 'explore_more' | 'publish';

export interface TrackComplementaryHostActionProps {
  eventId: string;
  stopId: string;
  action: ComplementaryHostAction;
  /** Number of stops in the draft at the time of the action. */
  stopCount: number;
}

/**
 * M-451: Fired for every host action on the ComplementaryReviewPanel
 * (keep, remove, replace selection, explore more, publish).
 */
export function trackComplementaryHostAction(props: TrackComplementaryHostActionProps): void {
  _track('Complementary Host Action', props);
}

export interface TrackComplementaryGenerationProps {
  eventId: string;
  /** Number of stops generated (0 = anchor-only fallback, 1 or 2 = additions present). */
  stopsGenerated: number;
  /** True when the anchor-only fallback was used (0 additions). */
  anchorOnlyFallback: boolean;
}

/**
 * M-451: Fired when the server returns generated complementary stops, including
 * the anchor-only fallback case so funnel drop-off is visible.
 */
export function trackComplementaryGeneration(props: TrackComplementaryGenerationProps): void {
  _track('Complementary Stops Generated', props);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- mixpanel Dict is { [key: string]: any }
function _track(event: string, properties?: Record<string, any>): void {
  if (!_initialised) return;
  try {
    mixpanel.track(event, properties);
  } catch {
    // Non-fatal.
  }
}
